import React from "react";
import Form from "./Form";

// 3-step "how it works". Inline SVG icons + an on-brand card, no stock photos,
// so it matches the rest of the premium redesign (replaced the old image cards).
const STEPS = [
  {
    title: "Search your route",
    desc: "Enter your pickup, drop, date and time to see live, fixed, all-inclusive fares.",
    icon: (<><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>),
  },
  {
    title: "Pick your cab",
    desc: "Choose a Hatchback, Sedan or SUV at a transparent price and pay a small advance.",
    icon: (<><path d="M5 17h14M5 17a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1l2-3h8l2 3h1a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2M5 17v2m14-2v2" /><circle cx="7.5" cy="17" r="0.6" /><circle cx="16.5" cy="17" r="0.6" /></>),
  },
  {
    title: "Ride & relax",
    desc: "A verified driver arrives on time. Track the trip live and pay the balance after.",
    icon: (<><path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3z" /><path d="m9 12 2 2 4-4" /></>),
  },
];

// Small inline icon helper for the hero points (stroke = currentColor).
const Ic = ({ d, sw = 1.8 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);
const IconCheck = () => <Ic d="M20 6 9 17l-5-5" sw={2.2} />;

const Main = ({ routePrefill }) => {
  return (
    <main className="home-main pm-home">
      <section className="pm-hero">
        <div className="pm-hero__bg" aria-hidden="true">
          <svg viewBox="0 0 1440 720" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="pm-hg" x1="0" y1="0" x2="0.4" y2="1">
                <stop offset="0" stopColor="#178f89" />
                <stop offset="1" stopColor="#0c5f5b" />
              </linearGradient>
            </defs>
            <rect width="1440" height="720" fill="url(#pm-hg)" />

            {/* soft depth circles */}
            <g fill="#ffffff" fillOpacity="0.06">
              <circle cx="1180" cy="150" r="160" />
              <circle cx="250" cy="108" r="95" />
            </g>

            {/* faint base current, keeps the lower band from reading empty */}
            <g fill="none" stroke="#ffffff" strokeOpacity="0.08" strokeWidth="2">
              <path d="M-40 600 C 300 500 520 660 820 560 S 1300 500 1500 590" />
            </g>

            {/* premium intercity route: a dashed journey threading location pins,
                kept to the left half so it decorates the copy side, not the form */}
            <g fill="none" stroke="#ffd9a8" strokeOpacity="0.30" strokeWidth="2.4" strokeLinecap="round" strokeDasharray="1.5 13">
              <path d="M44 636 C 210 556 330 556 470 594 S 620 624 704 552" />
            </g>
            <g fill="#ffd9a8" fillOpacity="0.34">
              <g transform="translate(44,636)"><path d="M0 0 C-8 -11 -8 -20 0 -20 C8 -20 8 -11 0 0 Z" /><circle cx="0" cy="-13" r="3.1" fill="#0c5f5b" fillOpacity="0.5" /></g>
              <g transform="translate(388,584)"><path d="M0 0 C-8 -11 -8 -20 0 -20 C8 -20 8 -11 0 0 Z" /><circle cx="0" cy="-13" r="3.1" fill="#0c5f5b" fillOpacity="0.5" /></g>
              <g transform="translate(704,552)"><path d="M0 0 C-8 -11 -8 -20 0 -20 C8 -20 8 -11 0 0 Z" /><circle cx="0" cy="-13" r="3.1" fill="#0c5f5b" fillOpacity="0.5" /></g>
            </g>

            {/* thin route-ruler dashes across the top */}
            <g fill="none" stroke="#ffd9a8" strokeOpacity="0.12" strokeWidth="2" strokeDasharray="2 12" strokeLinecap="round">
              <path d="M120 250 H1320" />
            </g>
          </svg>
        </div>

        <div className="pm-hero__grid">
          <div className="pm-hero__copy">
            <span className="pm-eyebrow">Cabs for every journey</span>
            {/* Deliberately not an <h1>: PopularRoutes below renders the page's
                single, keyword-rich <h1>. Styled identically to a heading. */}
            <div className="pm-hero__title">
              Your Ride. Your Route. <span>Your Way.</span>
            </div>
            <p className="pm-lead">
              Reliable cab bookings for airport transfers, local travel, rentals and outstation
              journeys, at a fixed, all-inclusive fare.
            </p>
            <ul className="pm-points">
              <li><IconCheck />Easy booking</li>
              <li><IconCheck />Transparent rates</li>
              <li><IconCheck />Verified drivers</li>
              <li><IconCheck />Multiple cab options</li>
            </ul>
          </div>

          {/* Booking widget: the shell is new, the <Form/> and all of its
              fields, validation, APIs and navigation are unchanged. */}
          <div className="pm-bw" id="book">
            <div className="pm-bw__head">
              <div>
                <h3>Book your ride</h3>
                <p>Choose your trip type and get started.</p>
              </div>
              <span className="pm-bw__live"><span className="pm-dot"></span>Live fares</span>
            </div>
            <Form routePrefill={routePrefill} />
          </div>
        </div>
      </section>

      {/* How it works, 3 clean on-brand steps (replaced the old stock-photo
          cards): teal icon tiles that fill on hover, coral accent, big ghost
          step numbers. */}
      <section className="home-how max-w-[1180px] mx-auto px-4 md:px-8 py-14 md:py-20">
        <div className="text-center max-w-[600px] mx-auto mb-10 md:mb-14">
          <span className="inline-block text-[11px] md:text-[12px] font-black uppercase tracking-[0.22em] text-[#21A5A0] mb-3">How it works</span>
          <h2 className="text-[28px] md:text-[42px] font-black tracking-tight text-[#16211f] leading-[1.08]">
            Simple. Easy. <span className="text-[#E05A49]">Reliable.</span>
          </h2>
          <p className="text-[14px] md:text-[16px] text-[#6b7280] mt-3">
            Book a cab in three quick steps, with a fixed, all-inclusive fare shown before you pay.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-7">
          {STEPS.map((s, i) => (
            <div
              key={s.title}
              className="group relative overflow-hidden bg-white rounded-[24px] border border-[#eef2f1] p-7 md:p-8 shadow-[0_4px_22px_rgba(16,40,37,0.05)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_14px_36px_rgba(33,165,160,0.13)] hover:border-[#21A5A0]/30"
            >
              <span className="pointer-events-none absolute -top-2 right-4 text-[64px] md:text-[80px] font-black leading-none text-[#f1f7f6] select-none">{`0${i + 1}`}</span>
              <span className="relative z-10 mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-[#d9ece9] bg-[#f0f6f5] text-[#21A5A0] transition-colors duration-300 group-hover:bg-[#21A5A0] group-hover:text-white">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  {s.icon}
                </svg>
              </span>
              <h3 className="relative z-10 mb-2 text-[18px] md:text-[20px] font-black text-[#16211f]">{s.title}</h3>
              <p className="relative z-10 text-[14px] md:text-[14.5px] leading-relaxed text-[#6b7280]">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default Main;
